import { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'

import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

type UnreadCount = { total: number; high_plus: number; critical: number }

// UnreadBadge is the bell icon that lives in the TopBar. Polls
// /notifications/unread-count every 30s (WebSocket already fires
// invalidations for stats — polling is fine for the badge itself).
// Click routes to /notifications-prefs.
export default function UnreadBadge() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])

  const q = useQuery({
    queryKey: ['notifications', 'unread-count'] as const,
    queryFn: () => client.call<UnreadCount>('/api/v1/notifications/unread-count'),
    refetchInterval: 30_000,
    staleTime: 15_000,
  })

  const total = q.data?.total ?? 0
  const critical = q.data?.critical ?? 0
  const active = critical > 0

  const tooltip = q.data
    ? `${q.data.total} unread · ${q.data.high_plus} high+ · ${q.data.critical} critical`
    : 'Notifications'

  return (
    <Tooltip title={tooltip}>
      <IconButton
        size="small"
        component={RouterLink}
        to="/notifications-prefs"
        aria-label={tooltip}
      >
        <Badge
          badgeContent={total > 0 ? total : null}
          max={99}
          color={active ? 'error' : 'warning'}
          overlap="circular"
        >
          {active ? <NotificationsActiveIcon sx={{ fontSize: 18 }} /> : <NotificationsIcon sx={{ fontSize: 18 }} />}
        </Badge>
      </IconButton>
    </Tooltip>
  )
}
